import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { Card } from '../../../components/ui/Card';
import { formatCurrency } from '../../../lib/utils';
import { CATEGORIAS_GASTO } from '../../../types/gasto';

interface DistribucionGastosProps {
  data: Array<{
    categoria: string;
    monto: number;
  }>;
  isLoading?: boolean;
}

const COLORS = ['#F59E0B', '#EF4444', '#3B82F6', '#8B5CF6', '#22C55E', '#EC4899', '#14B8A6', '#6B7280'];

export function DistribucionGastos({ data, isLoading = false }: DistribucionGastosProps) {
  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Cargando datos...</div>
        </div>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Distribución de Gastos</h3>
        <p className="text-sm text-gray-500 text-center py-8">No hay gastos registrados</p>
      </Card>
    );
  }

  const total = data.reduce((sum, d) => sum + d.monto, 0);

  // Obtener nombres de categorías
  const dataConNombres = data.map(item => {
    const cat = CATEGORIAS_GASTO.find(c => c.value === item.categoria);
    return {
      ...item,
      label: cat?.label || item.categoria,
      color: cat?.color || '#6B7280'
    };
  });

  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">Distribución de Gastos por Categoría</h3>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Gráfico de torta */}
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={dataConNombres}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }: any) => `${name || ''} ${((percent || 0) * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="monto"
                nameKey="label"
              >
                {dataConNombres.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color || COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => formatCurrency(Number(value))} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Lista de categorías */}
        <div className="space-y-2">
          {dataConNombres.map((item, index) => (
            <div key={index} className="flex items-center justify-between text-sm border-b border-gray-100 pb-2">
              <div className="flex items-center gap-2">
                <span
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: item.color || COLORS[index % COLORS.length] }}
                />
                <span className="text-gray-700">{item.label}</span>
              </div>
              <div className="text-right">
                <span className="font-medium text-gray-800">{formatCurrency(item.monto)}</span>
                <span className="text-xs text-gray-400 ml-2">
                  ({((item.monto / total) * 100).toFixed(0)}%)
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}